import { ErrorBoundary } from '@liveauctioneers/hammer-ui-core/providers';
import { MessageScroller, MessageScrollerProvider } from '@liveauctioneers/hammer-ui-liveauctioneers/messageScroller';
import { isEqual } from 'es-toolkit';

import { useAppSelector } from '@/redux/hooks';
import { getUserFullName } from '@/redux/modules/account/user/user.selectors';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import {
    getCatalogAbsenteeBidders,
    getCatalogCurrency,
    getCatalogSellerId,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogBidders } from '@/redux/modules/console';
import { getMessages } from '@/redux/modules/consoleMessages';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { getSellerLogoId } from '@/redux/modules/seller';
import { getAuctioneerLogoUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    className?: string;
    headerless?: boolean;
};

const MINIMUM_VIEWER_COUNT_WHEN_UNKNOWN = 1;

export const MessageScrollerContainer = ({ catalogId, className, headerless = false }: Props) => {
    const messageList = useAppSelector((state) => getMessages(state, catalogId, false), isEqual);
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const logoId = useAppSelector((state) => getSellerLogoId(state, sellerId));
    const userFullName = useAppSelector((state) => getUserFullName(state));

    const sourceCurrency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const preferredCurrency = useAppSelector(preferredCurrencySelector);
    const preferredCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, preferredCurrency));
    const sourceCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, sourceCurrency));

    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const viewerCount = Math.max(MINIMUM_VIEWER_COUNT_WHEN_UNKNOWN, bidders + absenteeBidders);

    return (
        <ErrorBoundary meta={{ name: 'MessageScroller' }}>
            <MessageScrollerProvider
                data={{
                    auctioneerInfo: {
                        logoUrl: getAuctioneerLogoUrl(logoId),
                    },
                    convert: true,
                    messages: messageList,
                    preferredCurrencyConversion,
                    sourceCurrencyConversion,
                    userName: userFullName,
                    viewerCount,
                }}
            >
                <MessageScroller
                    className={className}
                    headerless={headerless}
                />
            </MessageScrollerProvider>
        </ErrorBoundary>
    );
};
